// src/components/Navigation.js
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const Navigation = () => {
  // Drawer state for mobile menu
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Submenu open states
  const [membersSubmenuOpen, setMembersSubmenuOpen] = useState(false);
  const [faceSheetSubmenuOpen, setFaceSheetSubmenuOpen] = useState(false);

  // State for desktop submenus
  const [membersAnchorEl, setMembersAnchorEl] = useState(null);
  const [faceSheetAnchorEl, setFaceSheetAnchorEl] = useState(null);
  const [toolsAnchorEl, setToolsAnchorEl] = useState(null);

  // Drawer toggle handlers
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Handlers for desktop Members submenu
  const handleMembersClick = (event) => setMembersAnchorEl(event.currentTarget);
  const handleMembersClose = () => setMembersAnchorEl(null);

  // Handlers for Face Sheet submenu within Members (desktop)
  const handleFaceSheetClick = (event) =>
    setFaceSheetAnchorEl(event.currentTarget);
  const handleFaceSheetClose = () => setFaceSheetAnchorEl(null);

  // Handlers for Tools submenu within Tools (desktop)
  const handleToolsClick = (event) => setToolsAnchorEl(event.currentTarget);
  const handleToolsClose = () => setToolsAnchorEl(null);

  // Handlers for mobile Members submenu
  const handleMembersSubmenuToggle = () =>
    setMembersSubmenuOpen(!membersSubmenuOpen);

  // Handlers for mobile Face Sheet submenu
  const handleFaceSheetSubmenuToggle = () =>
    setFaceSheetSubmenuOpen(!faceSheetSubmenuOpen);

  // Drawer list for consistent menu structure (mobile)
  const drawerList = () => (
    <Box sx={{ width: 250 }} role="presentation">
      <List>
        {/* Mobile Members Submenu */}
        <ListItem button onClick={handleMembersSubmenuToggle}>
          <ListItemText primary="Members" />
          {membersSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={membersSubmenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem button component={NavLink} to="/members/full-list">
              <ListItemText primary="Full List" />
            </ListItem>
            <ListItem button component={NavLink} to="/members/ip-census">
              <ListItemText primary="IP Census" />
            </ListItem>
            <ListItem button onClick={handleFaceSheetSubmenuToggle}>
              <ListItemText primary="Face Sheet" />
              {faceSheetSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={faceSheetSubmenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  component={NavLink}
                  to="/members/face-sheet/profile"
                >
                  <ListItemText primary="Profile" />
                </ListItem>
                <ListItem
                  button
                  component={NavLink}
                  to="/members/face-sheet/diagnosis-codes"
                >
                  <ListItemText primary="Diagnosis Codes" />
                </ListItem>
                <ListItem
                  button
                  component={NavLink}
                  to="/members/face-sheet/claims"
                >
                  <ListItemText primary="Claims" />
                </ListItem>
                <ListItem
                  button
                  component={NavLink}
                  to="/members/face-sheet/opportunities"
                >
                  <ListItemText primary="Opportunities" />
                </ListItem>
              </List>
            </Collapse>
          </List>
        </Collapse>

        {/* Other Navigation Items */}
        <ListItem button component={NavLink} to="/office">
          <ListItemText primary="Office" />
        </ListItem>
        <ListItem button component={NavLink} to="/ip-census-management">
          <ListItemText primary="IP Census Management" />
        </ListItem>
        <ListItem button component={NavLink} to="/messages">
          <ListItemText primary="Messages" />
        </ListItem>
        <ListItem button component={NavLink} to="/shared-files">
          <ListItemText primary="Shared Files" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <div>
      <AppBar position="static">
        <Toolbar>
          {/* Menu icon for the Drawer (mobile) */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ display: { xs: "block", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Left-aligned navigation for desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 2,
              flexGrow: 1,
            }}
          >
            {/* Members submenu (desktop) */}

            <Menu
              anchorEl={membersAnchorEl}
              open={Boolean(membersAnchorEl)}
              onClose={handleMembersClose}
            >
              <MenuItem
                component={NavLink}
                to="/members/full-list"
                onClick={handleMembersClose}
              >
                Full List
              </MenuItem>
              <MenuItem
                component={NavLink}
                to="/members/ip-census"
                onClick={handleMembersClose}
              >
                IP Census
              </MenuItem>
              {/* Face Sheet Submenu */}
              <MenuItem
                onClick={handleFaceSheetClick}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                Face Sheet
                <ChevronRightIcon />
              </MenuItem>
              <Menu
                anchorEl={faceSheetAnchorEl}
                open={Boolean(faceSheetAnchorEl)}
                onClose={handleFaceSheetClose}
                keepMounted
              >
                <MenuItem
                  component={NavLink}
                  to="/members/face-sheet/profile"
                  onClick={handleFaceSheetClose}
                >
                  Profile
                </MenuItem>
                <MenuItem
                  component={NavLink}
                  to="/members/face-sheet/diagnosis-codes"
                  onClick={handleFaceSheetClose}
                >
                  Diagnosis Codes
                </MenuItem>
                <MenuItem
                  component={NavLink}
                  to="/members/face-sheet/claims"
                  onClick={handleFaceSheetClose}
                >
                  Claims
                </MenuItem>
                <MenuItem
                  component={NavLink}
                  to="/members/face-sheet/opportunities"
                  onClick={handleFaceSheetClose}
                >
                  Opportunities
                </MenuItem>
              </Menu>
            </Menu>

            {/* Tools with IP Census Management */}
            <Button
              color="inherit"
              // onClick={handleToolsClick}
              style={{ textTransform: "none" }}
              component={NavLink}
              to="/ip-census-management"
              onClick={handleToolsClose}
            >
              IP Census Management
            </Button>
            {/* <Menu
              anchorEl={toolsAnchorEl}
              open={Boolean(toolsAnchorEl)}
              onClose={handleToolsClose}
            >
              <MenuItem
                component={NavLink}
                to="/tools/ip-census-management"
                onClick={handleToolsClose}
              >
                IP Census Management
              </MenuItem>
            </Menu> */}

            {/* Other desktop navigation buttons */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer Component (Mobile Menu) */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {drawerList()}
      </Drawer>
    </div>
  );
};

export default Navigation;
